import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { AgentFocus } from '@tunasong/agent-context'

type tools = [
  'Settings',
  'Chat',
  'Activities',
  'Agents',
  'My Notes',
  'Pool',
  'Context',
  'Suggestions',
  'Record',
  'Guitar',
  'Debug',
]
export type SidebarToolName = tools[number]

export interface ComposerIntent {
  /** Agent whose composer this intent targets. */
  agentId: string
  /** Text to place in the composer (or send, when `autoSend`). */
  draft?: string
  /** Send `draft` immediately as a turn instead of pre-filling. */
  autoSend?: boolean
  /** Block anchor to attach to the outgoing turn. */
  focus?: AgentFocus
  /** Monotonic per-dispatch counter — a re-dispatched identical intent still re-triggers consumers. */
  nonce: number
}

// Define a type for the slice state
export interface UIState {
  sidebar: {
    open: boolean
    activeTool: SidebarToolName | null
    /**
     * Id of the agent whose chat thread is currently surfaced in the Agents
     * sidebar tool. Set by the header Agents pill and the in-panel Tabs. Cleared when `activeTool` changes away
     * from `'Agents'` so the next time the panel opens it starts fresh.
     */
    activeAgentId: string | null
    /**
     * Atomic composer hand-off (Phase 2). Set by `openAgentComposer` — the ONLY
     * writer — and consumed exactly once by `AgentChat`, which copies it into
     * local state (focus chip, composer seed) and dispatches
     * `clearComposerIntent`. Redux is the transport; the component owns the
     * lifetime.
     */
    composerIntent: ComposerIntent | null
    /** Monotonic dispatch counter feeding `ComposerIntent.nonce`. Never resets. */
    composerIntentSeq: number
  }
  /**
   * Cross-component coordination for inline-anchored personal notes. When a user
   * clicks an anchor marker (or just-created their note), the marker handler sets
   * this id; the My Notes panel reads it on mount, expands the matching row, and
   * clears it via `setPendingAnchoredNoteId(null)`.
   */
  pendingAnchoredNoteId: string | null
  /**
   * Set alongside `pendingAnchoredNoteId` when the note was opened from its backlink
   * (`?ref=<noteId>`, GH #1579): the My Notes panel then also scrolls the editor to the
   * note's anchored block. The marker-click path leaves it false — the user is already
   * looking at that block.
   */
  pendingNoteScroll: boolean
  /**
   * Element id of the block whose personal note is currently expanded in the
   * sidebar. Drives the editor-side `data-anchored-active` highlight and the
   * 600ms spotlight pulse. `null` when no note is expanded (or the expanded
   * note has no `targetElementId`).
   */
  activeAnchoredElementId: string | null
  /**
   * Element id previewed by the sidebar's hover or focus on a note row. Drives
   * a lighter `data-anchored-hover` highlight without scrolling. Cleared on
   * mouseleave/blur so the editor returns to the baseline anchored halo.
   */
  hoveredAnchoredElementId: string | null
  /**
   * Element ids of all blocks that are the target of at least one personal
   * note in the current entity. Maintained by the personal-note plugin's
   * `AnchoredNotesController` and read by the editor's `useAnchoredState` to
   * render the subtle baseline halo on every anchored block. Stored as an
   * array for Redux Toolkit / Immer compatibility — readers convert to a Set
   * if they need O(1) lookup.
   */
  anchoredElementIds: string[]
}

// Define the initial state using that type
const initialState: UIState = {
  sidebar: {
    open: false,
    activeTool: null,
    activeAgentId: null,
    composerIntent: null,
    composerIntentSeq: 0,
  },
  pendingAnchoredNoteId: null,
  pendingNoteScroll: false,
  activeAnchoredElementId: null,
  hoveredAnchoredElementId: null,
  anchoredElementIds: [],
}

/**
 * @note if you get an Immer error here, pin the Immer version to ensure a single instance
 * @see https://github.com/reduxjs/redux-toolkit/issues/1181
 */
export const uiSlice = createSlice({
  name: 'ui',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setSidebarActiveTool: (state, action: PayloadAction<SidebarToolName | null>) => {
      state.sidebar.activeTool = action.payload
      state.sidebar.open = action.payload === null ? false : true
      if (action.payload !== 'Agents') {
        state.sidebar.activeAgentId = null
        state.sidebar.composerIntent = null
      }
    },
    toggleSidebarActiveTool: (state, action: PayloadAction<SidebarToolName>) => {
      state.sidebar.activeTool = state.sidebar.activeTool === action.payload ? null : action.payload
      state.sidebar.open = state.sidebar.activeTool === null ? false : true
      if (state.sidebar.activeTool !== 'Agents') {
        state.sidebar.activeAgentId = null
        state.sidebar.composerIntent = null
      }
    },

    toggleSidebar: state => {
      if (state.sidebar.activeTool === null) {
        state.sidebar.activeTool = 'Settings'
        state.sidebar.open = true
      } else {
        state.sidebar.activeTool = null
        state.sidebar.open = false
        state.sidebar.activeAgentId = null
        state.sidebar.composerIntent = null
      }
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.open = action.payload
    },
    setSidebarActiveAgentId: (state, action: PayloadAction<string | null>) => {
      state.sidebar.activeAgentId = action.payload
    },
    openAgentComposer: (state, action: PayloadAction<Omit<ComposerIntent, 'nonce'>>) => {
      state.sidebar.activeTool = 'Agents'
      state.sidebar.open = true
      state.sidebar.activeAgentId = action.payload.agentId
      state.sidebar.composerIntentSeq += 1
      state.sidebar.composerIntent = { ...action.payload, nonce: state.sidebar.composerIntentSeq }
    },
    clearComposerIntent: state => {
      state.sidebar.composerIntent = null
    },
    setPendingAnchoredNoteId: (state, action: PayloadAction<string | null>) => {
      state.pendingAnchoredNoteId = action.payload
    },
    setPendingNoteScroll: (state, action: PayloadAction<boolean>) => {
      state.pendingNoteScroll = action.payload
    },
    setActiveAnchoredElementId: (state, action: PayloadAction<string | null>) => {
      state.activeAnchoredElementId = action.payload
    },
    setHoveredAnchoredElementId: (state, action: PayloadAction<string | null>) => {
      state.hoveredAnchoredElementId = action.payload
    },
    setAnchoredElementIds: (state, action: PayloadAction<string[]>) => {
      state.anchoredElementIds = action.payload
    },
  },
})
